import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { CreditCard, Search, Users, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  valid: { label: 'Valide', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  verification: { label: 'En vérification', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  expired: { label: 'Expiré', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

export default function AnalystMyRIBs() {
  const { token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [ribs, setRibs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchRibs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/iban-pool/my-ribs`, { headers: h });
      setRibs(res.data || []);
    } catch (e) {
      toast.error('Erreur chargement des RIB');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRibs(); }, [fetchRibs]);

  const filtered = ribs.filter(r => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return r.iban?.toLowerCase().includes(q) || r.account_holder?.toLowerCase().includes(q) || r.bank_name?.toLowerCase().includes(q);
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analyst-my-ribs">
        <div>
          <h1 className="text-2xl font-bold">Mes RIB</h1>
          <p className="text-sm text-muted-foreground">Liste des RIB qui vous ont été attribués par l'administration</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher..." value={filter} onChange={e => setFilter(e.target.value)} className="pl-9" />
          </div>
          <Badge variant="outline">{filtered.length} RIB</Badge>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <CreditCard className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-muted-foreground">Aucun RIB attribué</p>
            <p className="text-xs text-muted-foreground mt-1">L'administration doit vous attribuer des RIB depuis la gestion centralisée</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(rib => {
              const st = STATUS_MAP[rib.status] || STATUS_MAP.valid;
              const isExpanded = expanded === rib.id;
              return (
                <div key={rib.id} className="border rounded-xl overflow-hidden">
                  <button onClick={() => setExpanded(isExpanded ? null : rib.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-muted/10 transition-colors text-left">
                    <div className="flex items-center gap-4 min-w-0">
                      <CreditCard className="w-5 h-5 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="font-mono text-sm truncate">{rib.iban}</p>
                        <p className="text-xs text-muted-foreground">{rib.account_holder} {rib.bank_name ? `— ${rib.bank_name}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="outline" className={st.color}>{st.label}</Badge>
                      <Badge variant="secondary" className="text-xs">
                        <Users className="w-3 h-3 mr-1" />{(rib.assigned_clients || []).length} client{(rib.assigned_clients || []).length !== 1 ? 's' : ''}
                      </Badge>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="border-t px-4 py-3 bg-muted/5">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm mb-3">
                        <div><span className="text-xs text-muted-foreground">BIC</span><p className="font-mono">{rib.bic || '-'}</p></div>
                        <div><span className="text-xs text-muted-foreground">Banque</span><p>{rib.bank_name || '-'}</p></div>
                        <div><span className="text-xs text-muted-foreground">Pays</span><p>{rib.country || '-'}</p></div>
                        <div><span className="text-xs text-muted-foreground">Statut</span><p><Badge variant="outline" className={st.color}>{st.label}</Badge></p></div>
                      </div>
                      {(rib.client_names || []).length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">Clients utilisant ce RIB :</p>
                          <div className="flex flex-wrap gap-2">
                            {rib.client_names.map(c => (
                              <Badge key={c.id} variant="outline">{c.name}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {(rib.client_names || []).length === 0 && (
                        <p className="text-xs text-muted-foreground">Aucun client n'utilise ce RIB pour le moment</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
